<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import type { Tender } from '@/types/tender'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import TextareaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'

interface Agent {
  id: string
  name: string
  email: string
  role: string
  first_name?: string
  last_name?: string
  phone?: string
  department?: string
  company?: string  // ADD THIS - Company field
  created_at?: string
  updated_at?: string
}

interface Props {
  tender: Tender
  currentAgentId?: string
}

interface Emits {
  (
    e: 'assign',
    data: {
      tenderId: string
      agentId: string
      agentName: string
      notes: string
    },
  ): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Form state
const selectedAgentId = ref('')
const assignmentNotes = ref('')

// Agent data
const agents = ref<Agent[]>([])
const loadingAgents = ref(false)
const agentError = ref<string | null>(null)

// Form validation
const agentValidationError = ref('')
const notesValidationError = ref('')

// Define proper types for agent data
interface ServerAgent {
  id: number
  username: string
  email: string
  role: string
  first_name?: string
  last_name?: string
  phone?: string
  department?: string
  company?: string  // ADD THIS - Company field
  created_at?: string
  updated_at?: string
}

// Fetch agents from server using centralized api
const fetchAgents = async () => {
  try {
    console.log('🔄 Fetching agents from server...')
    agentError.value = null
    loadingAgents.value = true

    const response = await api.get('/api/users/agents')

    console.log('📥 Agents API response:', {
      status: response.status,
      data: response.data,
      success: response.data.success,
      agentsCount: response.data.agents?.length,
    })

    if (response.data.success && response.data.agents) {
      // Transform the data to match your Agent interface
      agents.value = response.data.agents.map((agent: ServerAgent) => ({
        id: agent.id.toString(),
        name:
          `${agent.first_name || ''} ${agent.last_name || ''}`.trim() ||
          agent.username ||
          agent.email,
        email: agent.email,
        role: agent.role,
        first_name: agent.first_name,
        last_name: agent.last_name,
        phone: agent.phone,
        department: agent.department,
        company: agent.company,  // ADD THIS - Map company field
        created_at: agent.created_at,
        updated_at: agent.updated_at,
      }))
      console.log(`✅ Found ${agents.value.length} agents`)
    } else {
      throw new Error(response.data.error || 'No agents found')
    }
  } catch (err) {
    console.error('❌ Error fetching agents:', err)
    agentError.value = getAxiosErrorMessage(err)
    agents.value = []
  } finally {
    loadingAgents.value = false
  }
}

// Helper to get company display name
const getCompanyDisplay = (company: string | undefined): string => {
  if (!company) return 'No Company'

  const companies: Record<string, string> = {
    'maxstationary': 'Max Stationary',
    'ostnasik': 'OST Nasik',
    'shraddha-ent': 'Shraddha Enterprises'
  }

  return companies[company] || company
}

// Convert agents to SelectOptionPrime format - EXCLUDE CURRENT AGENT
const agentOptions = computed(() => {
  return agents.value
    .filter((agent) => agent.id !== props.currentAgentId) // Filter out current agent
    .map((agent) => ({
      label: `${agent.name} (${agent.email}) - ${getCompanyDisplay(agent.company)}`, // UPDATED: Include company in label
      value: agent.id,
      description: agent.role === 'agent' ? 'Agent' : agent.role,
      disabled: agent.id === props.currentAgentId, // Mark as disabled if it's current agent
    }))
})

// Get current agent details
const currentAgent = computed(() => {
  if (!props.currentAgentId) return null
  return agents.value.find((agent) => agent.id === props.currentAgentId)
})

// Format date for display
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Format number
const formatNumber = (num: number): string => {
  if (num >= 10000000) return (num / 10000000).toFixed(2) + ' Cr'
  if (num >= 100000) return (num / 100000).toFixed(2) + ' L'
  if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  return num.toLocaleString('en-IN')
}

// Get days remaining for tender
const getDaysRemaining = (deadline: string): string => {
  if (!deadline) return 'No deadline'
  try {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    const diffDays = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return 'Expired'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    return `${diffDays} days left`
  } catch {
    return 'Date error'
  }
}

// Get selected agent details
const selectedAgent = computed(() => {
  return agents.value.find((agent) => agent.id === selectedAgentId.value)
})

// Validation functions
const validateAgent = () => {
  if (!selectedAgentId.value) {
    agentValidationError.value = 'Please select an agent'
    return false
  }
  agentValidationError.value = ''
  return true
}

const validateNotes = () => {
  // Notes are optional - no validation required
  notesValidationError.value = ''
  return true
}

// Combined validation - exposed to parent
const validateForm = () => {
  const isAgentValid = validateAgent()
  const isNotesValid = validateNotes() // Always returns true now
  return isAgentValid && isNotesValid
}

// Submit function - exposed to parent
const submitForm = () => {
  const isValid = validateForm()

  if (!isValid) {
    return false // Validation failed
  }

  // Get the selected agent's name
  const selectedAgentObj = agents.value.find((agent) => agent.id === selectedAgentId.value)
  const agentName = selectedAgentObj?.name || `Agent ${selectedAgentId.value.substring(0, 8)}`

  // Emit the assign event with validated data INCLUDING agentName
  emit('assign', {
    tenderId: props.tender.t247Id,
    agentId: selectedAgentId.value,
    agentName: agentName,
    notes: assignmentNotes.value, // Can be empty string
  })

  return true // Submission successful
}

// Handle agent selection change
const handleAgentChange = (value: string) => {
  selectedAgentId.value = value
  // Clear error when user selects an agent
  if (value) {
    agentValidationError.value = ''
  }
}

// Handle notes change
const handleNotesChange = (value: string) => {
  assignmentNotes.value = value
  // No validation needed
}

// Initialize - fetch agents
onMounted(() => {
  console.log('🔍 AgentAssignmentModal mounted')
  fetchAgents()
})

// Watch for tender changes
watch(
  () => props.tender,
  (newTender) => {
    if (newTender) {
      // Clear form when new tender is selected
      selectedAgentId.value = ''
      assignmentNotes.value = ''
      agentValidationError.value = ''
      notesValidationError.value = '' // Always empty now
    }
  },
)

// Watch for currentAgentId changes
watch(
  () => props.currentAgentId,
  (newCurrentAgentId) => {
    console.log('Current agent ID changed:', newCurrentAgentId)
    if (newCurrentAgentId) {
      // Clear selected agent if it's the same as current agent
      if (selectedAgentId.value === newCurrentAgentId) {
        selectedAgentId.value = ''
      }
    }
  },
)

// Expose methods to parent component
defineExpose({
  validateForm,
  submitForm,
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Tender Information -->
    <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
      <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Tender Details</h4>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Reference No</p>
          <p class="text-sm font-medium">{{ tender.referenceNo }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Deadline</p>
          <p class="text-sm font-medium">{{ getDaysRemaining(tender.deadline as string) }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Organization</p>
          <p class="text-sm font-medium">{{ tender.organization }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Estimated Cost</p>
          <p class="text-sm font-medium">₹{{ formatNumber(tender.estimatedCost) }}</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <LoaderDefault
      v-if="loadingAgents"
      title="Loading agents from database..."
      subtitle="Please wait while we fetch available agents"
    />

    <!-- Agent Selection -->
    <div class="space-y-3" v-else-if="!agentError && agents.length > 0">
      <div class="flex items-center justify-between">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Select Agent <span class="text-red-500">*</span>
        </label>
        <span class="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
          {{ agents.length }} Agents Available
        </span>
      </div>

      <!-- Show current agent info if exists -->
      <div
        v-if="currentAgent && currentAgentId"
        class="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3"
      >
        <p class="text-sm font-medium text-amber-800 mb-1">Currently Assigned Agent:</p>
        <p class="text-sm text-amber-700">{{ currentAgent.name }} ({{ currentAgent.email }})</p>
        <p class="text-xs text-amber-600">
          Company: {{ getCompanyDisplay(currentAgent.company) }}
        </p>
        <p class="text-xs text-amber-600 mt-1">
          This agent is already assigned to this tender and cannot be selected again.
        </p>
      </div>

      <SelectOptionPrime
        v-model="selectedAgentId"
        :options="agentOptions"
        id="agentSelect"
        placeholder="-- Select an agent --"
        :asterisk="true"
        :errorMessage="agentValidationError"
        @update:modelValue="handleAgentChange"
        parentClass="w-full"
        size="md"
        :disabled="loadingAgents"
      />

      <!-- Selected Agent Details - UPDATED to show company -->
      <div v-if="selectedAgent" class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p class="text-sm font-medium text-blue-800 mb-1">New Agent to Assign:</p>
        <p class="text-sm text-blue-700">{{ selectedAgent.name }} ({{ selectedAgent.email }})</p>
        <p class="text-xs text-blue-600">Company: {{ getCompanyDisplay(selectedAgent.company) }}</p>
        <p class="text-xs text-blue-600">Phone: {{ selectedAgent.phone || 'N/A' }}</p>
        <p class="text-xs text-blue-600" v-if="selectedAgent.created_at">
          Member since: {{ formatDate(selectedAgent.created_at) }}
        </p>
      </div>
    </div>

    <!-- No Agents Available (excluding current agent) -->
    <div
      v-else-if="!loadingAgents && !agentError && agents.length === 1 && currentAgent"
      class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
    >
      <p class="text-sm text-yellow-700 font-medium mb-1">Only Current Agent Available</p>
      <p class="text-sm text-yellow-600">
        The only agent available is already assigned to this tender. Please add more agents to the
        system to reassign this tender.
      </p>
      <div class="flex gap-2 mt-2">
        <button
          @click="fetchAgents"
          class="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- No Agents Available (general) -->
    <div
      v-else-if="!loadingAgents && !agentError && agents.length === 0"
      class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
    >
      <p class="text-sm text-yellow-700 font-medium mb-1">No Agents Available</p>
      <p class="text-sm text-yellow-600">
        No active agents found in the database. Please add agents before assigning tenders.
      </p>
      <div class="flex gap-2 mt-2">
        <button
          @click="fetchAgents"
          class="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- Agent Error State -->
    <div v-else-if="agentError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm font-medium text-red-800 mb-2">Failed to Load Agents</p>
      <p class="text-sm text-red-700 mb-3">{{ agentError }}</p>

      <div class="flex flex-col gap-2 mt-3">
        <div class="flex gap-2">
          <button
            @click="fetchAgents"
            class="flex-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors"
          >
            Retry
          </button>
        </div>

        <div class="flex gap-2">
          <button
            @click="$emit('cancel')"
            class="flex-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Assignment Notes (OPTIONAL now) - only show if agents loaded successfully -->
    <div v-if="!loadingAgents && !agentError && agents.length > 0" class="space-y-2">
      <label
        for="assignmentNotes"
        class="block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        Assignment Notes <span class="text-gray-400 text-xs">(Optional)</span>
      </label>
      <TextareaDefault
        v-model="assignmentNotes"
        id="assignmentNotes"
        name="assignmentNotes"
        placeholder="Enter any notes about this assignment (optional)..."
        :rows="4"
        :errorMessage="notesValidationError"
        @update:modelValue="handleNotesChange"
        parentClass="w-full"
      />
      <p class="text-xs text-gray-500">
        These notes will be visible to the assigned agent if provided.
      </p>
    </div>

    <!-- Validation Summary -->
    <div
      v-if="agentValidationError"
      class="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
    >
      <p class="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
        Please fix the following errors:
      </p>
      <ul class="text-xs text-red-600 dark:text-red-400 space-y-1">
        <li v-if="agentValidationError">• {{ agentValidationError }}</li>
      </ul>
    </div>

    <!-- Instructions -->
    <!-- <div
      v-if="!loadingAgents && !agentError && agents.length > 0"
      class="p-3 bg-gray-50 border border-gray-200 rounded-lg"
    >
      <p class="text-sm font-medium text-gray-700 mb-1">Assignment Instructions:</p>
      <ul class="text-xs text-gray-600 space-y-1">
        <li>• Once assigned, this tender will move to the Agent Tenders page</li>
        <li>• The agent will receive a notification about this assignment</li>
        <li>• The tender will no longer appear in the main dashboard list</li>
        <li>• You can track progress in the Agent Tenders section</li>
        <li v-if="currentAgent">• Current agent will be replaced with the new agent</li>
        <li>• The company name will be automatically set based on the selected agent</li>
      </ul>
    </div> -->

    <!-- Cannot Assign Warning -->
    <div
      v-if="!loadingAgents && !agentError && agents.length === 0"
      class="p-4 bg-red-50 border border-red-200 rounded-lg"
    >
      <p class="text-sm font-medium text-red-800 mb-2">Cannot Assign Tender</p>
      <p class="text-sm text-red-700">
        You cannot assign this tender because there are no active agents available. Please add
        agents to the system first.
      </p>
    </div>
  </div>
</template>
